import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createThread,
  fetchForumIndex,
  type ForumIndexResponse,
  type ForumThreadSummary,
} from "../api/forumApi";
import { useForumIdentity } from "../hooks/useForumIdentity";
import { ForumIdentityBar } from "../components/ForumIdentityBar";
import { ForumAvatar } from "../components/ForumText";
import { formatRelativeTime } from "../utils/time";

const PAGE_SIZE = 25;

/**
 * The board index: categories down one side, threads down the middle,
 * a composer for starting a new one. Everything is public and readable
 * without a wallet; posting needs nothing but a body (see forum-post.ts
 * for the deliberately-open policy and the spam controls that make it
 * survivable).
 */
export function Forum() {
  const identity = useForumIdentity();
  const [data, setData] = useState<ForumIndexResponse | null>(null);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [postCategory, setPostCategory] = useState("general");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [justPosted, setJustPosted] = useState<string | null>(null);
  // Same optimistic trick the thread view uses: a thread you just started
  // may not be in the server's index read for a second or two.
  const [pendingThreads, setPendingThreads] = useState<ForumThreadSummary[]>([]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchForumIndex("all", identity.account);
      setData(res);
      setPendingThreads((prev) => prev.filter((p) => !res.threads.some((t) => t.id === p.id)));
      if (res.categories.length && !res.categories.some((c) => c.id === postCategory)) {
        setPostCategory(res.categories[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the forum.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity.account]);

  const threads = useMemo(() => {
    const server = data?.threads ?? [];
    const all = [...pendingThreads.filter((p) => !server.some((t) => t.id === p.id)), ...server];
    const byCategory = category === "all" ? all : all.filter((t) => t.categoryId === category);
    const q = search.trim().toLowerCase();
    if (!q) return byCategory;
    return byCategory.filter(
      (t) => t.title.toLowerCase().includes(q) || t.author.toLowerCase().includes(q)
    );
  }, [data, pendingThreads, category, search]);

  const pageThreads = threads.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(threads.length / PAGE_SIZE));

  async function submitThread() {
    const t = title.trim();
    const b = body.trim();
    if (!t || !b) return;
    setPosting(true);
    setPostError(null);
    try {
      const res = await createThread({
        categoryId: postCategory,
        title: t,
        body: b,
        author: identity.displayName,
        isWallet: identity.isWallet,
      });
      const nowIso = new Date().toISOString();
      setPendingThreads((prev) => [
        {
          id: res.threadId,
          categoryId: postCategory,
          title: t,
          author: identity.displayName || "anon",
          isWallet: identity.isWallet,
          createdAt: nowIso,
          lastActivityAt: nowIso,
          lastAuthor: identity.displayName || "anon",
          replyCount: 0,
          pinned: false,
          locked: false,
          hidden: false,
          views: 0,
        },
        ...prev,
      ]);
      setTitle("");
      setBody("");
      setComposing(false);
      setJustPosted(res.threadId);
      await load();
    } catch (err) {
      setPostError(err instanceof Error ? err.message : "Couldn't post that.");
    } finally {
      setPosting(false);
    }
  }

  const categoryName = (id: string) => data?.categories.find((c) => c.id === id)?.name ?? id;

  return (
    <div className="page">
      <div className="page__header">
        <h1>Forum</h1>
        <p className="page__subtitle">
          Threaded discussion for everything WAX — creating, backing, dropping and trading. No signup: post under
          your WAX account, a display name, or just anonymously.
        </p>
      </div>

      {data?.forumLocked && (
        <div className="callout callout--warning" style={{ marginBottom: 20 }}>
          🔒 The forum is read-only right now.
        </div>
      )}

      <div className="forum-layout">
        <aside className="forum-sidebar">
          <button
            className={`forum-category${category === "all" ? " forum-category--active" : ""}`}
            onClick={() => {
              setCategory("all");
              setPage(0);
            }}
          >
            <span className="forum-category__name">All discussions</span>
            <span className="forum-category__count">{data?.totals.threads ?? 0}</span>
          </button>
          {(data?.categories ?? []).map((c) => (
            <button
              key={c.id}
              className={`forum-category${category === c.id ? " forum-category--active" : ""}`}
              onClick={() => {
                setCategory(c.id);
                setPage(0);
              }}
              title={c.description}
            >
              <span className="forum-category__name">{c.name}</span>
              <span className="forum-category__count">{data?.counts[c.id] ?? 0}</span>
            </button>
          ))}
          {data && (
            <p className="forum-sidebar__stats">
              {data.totals.threads} thread{data.totals.threads === 1 ? "" : "s"} · {data.totals.posts} post
              {data.totals.posts === 1 ? "" : "s"}
            </p>
          )}
        </aside>

        <section className="forum-main">
          <div className="forum-toolbar">
            <input
              type="text"
              className="forum-search"
              placeholder="Search threads…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
            {!data?.forumLocked && (
              <button className="btn btn--primary" onClick={() => setComposing((c) => !c)}>
                {composing ? "Cancel" : "Start a thread"}
              </button>
            )}
          </div>

          {composing && (
            <div className="forum-composer forum-composer--new">
              <ForumIdentityBar identity={identity} />
              <input
                type="text"
                placeholder="Thread title"
                maxLength={140}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <select value={postCategory} onChange={(e) => setPostCategory(e.target.value)}>
                {(data?.categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <textarea
                rows={7}
                maxLength={5000}
                placeholder="What's on your mind? Links are clickable, and a line starting with > renders as a quote."
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              {postError && <div className="forum-inline-error">{postError}</div>}
              <div className="forum-composer__actions">
                <button className="btn btn--primary" onClick={submitThread} disabled={posting || !title.trim() || !body.trim()}>
                  {posting ? "Posting…" : "Post thread"}
                </button>
                <span className="forum-composer__hint">Public and permanent-ish — don't post anything private.</span>
              </div>
            </div>
          )}

          {justPosted && (
            <div className="callout" style={{ marginBottom: 16 }}>
              Thread posted. <Link to={`/forum/t/${justPosted}`} className="forum-text__link">Open it →</Link>
            </div>
          )}

          {loading && <div className="state-message">Loading the board…</div>}
          {error && <div className="state-message state-message--error">{error}</div>}

          {!loading && !error && pageThreads.length === 0 && (
            <div className="state-message">
              Nothing here yet. {data?.forumLocked ? "" : "Start the first thread."}
            </div>
          )}

          <div className="forum-threads">
            {pageThreads.map((t: ForumThreadSummary) => (
              <Link key={t.id} to={`/forum/t/${t.id}`} className="forum-thread-row">
                <ForumAvatar name={t.author} isWallet={t.isWallet} />
                <div className="forum-thread-row__body">
                  <div className="forum-thread-row__title">
                    {t.pinned && <span className="forum-tag forum-tag--pin">Pinned</span>}
                    {t.locked && <span className="forum-tag">Locked</span>}
                    {t.hidden && <span className="forum-tag forum-tag--hidden">Hidden</span>}
                    {t.title}
                  </div>
                  <div className="forum-thread-row__meta">
                    <span className="forum-tag forum-tag--category">{categoryName(t.categoryId)}</span>
                    <span>{t.author || "anon"}</span>
                    <span>· {formatRelativeTime(new Date(t.createdAt).getTime())}</span>
                  </div>
                </div>
                <div className="forum-thread-row__stats">
                  <span className="forum-thread-row__count">{t.replyCount}</span>
                  <span className="forum-thread-row__label">
                    {t.replyCount === 1 ? "reply" : "replies"}
                  </span>
                  <span className="forum-thread-row__last">
                    last: {formatRelativeTime(new Date(t.lastActivityAt || t.createdAt).getTime())}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {pageCount > 1 && (
            <div className="forum-pager">
              <button className="btn btn--outline btn--small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                ← Newer
              </button>
              <span>
                Page {page + 1} of {pageCount}
              </span>
              <button
                className="btn btn--outline btn--small"
                disabled={page + 1 >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                Older →
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
